import moment from 'moment-timezone';

import { DATE_DAILY_SHORT_FORMAT } from '../../constants/date';
import { groupBy as groupByUtil } from '../../utils/collection';
import { types } from '../../constants/filter';
import { binarySearch, getRange, unwrapRows, toCohortFormat } from '../../utils/chart/converter';

function createEmptyRows(info, firstRow, dates) {
  const rows = [];
  for (let i = 0; i < dates.length; i++) {
    rows.push({
      data: { ...firstRow, [info.groupBy]: dates[i].valueOf(), [info.aggregation[0]]: 0 },
    });
  }
  return rows;
}

export function groupBySplitWithDates(data, info) {
  const dates = getRange(info);
  const groups = {};

  if (!data.length) {
    const groupName = undefined;
    groups[groupName] = createEmptyRows(info, {}, dates);
  }

  for (let i = 0; i < data.length; i++) {
    const groupName = data[i][info.splitBy];
    if (groups[groupName] === undefined) {
      groups[groupName] = createEmptyRows(info, data[i], dates);
    }
    const row = binarySearch(
      groups[groupName],
      (r) => data[i][info.groupBy] - r.data[info.groupBy]
    );
    row.data = data[i];
  }
  const groupEntries = Object.entries(groups);
  const result = {};
  for (let i = 0; i < groupEntries.length; i++) {
    result[groupEntries[i][0]] = unwrapRows(groupEntries[i][1]);
  }
  return result;
}

function createEmptyRowsCSV(info, firstRow, dates) {
  const rows = [];
  for (let i = 0; i < dates.length; i++) {
    const obj = {};
    Object.keys(firstRow).forEach((key) => {
      obj[key] = '';
    });

    rows.push({
      data: { ...obj, [info.groupBy]: dates[i].valueOf(), [info.aggregation[0]]: '' },
    });
  }
  return rows;
}

export function groupBySplitWithDatesCSV(data, info) {
  const dates = getRange(info);
  const groups = {};

  if (!data.length) {
    const groupName = undefined;
    groups[groupName] = createEmptyRowsCSV(info, {}, dates);
  }

  for (let i = 0; i < data.length; i++) {
    const groupName = data[i][info.splitBy];
    if (groups[groupName] === undefined) {
      groups[groupName] = createEmptyRowsCSV(info, data[i], dates);
    }
    const row = binarySearch(
      groups[groupName],
      (r) => data[i][info.groupBy] - r.data[info.groupBy]
    );
    row.data = data[i];
  }
  const groupEntries = Object.entries(groups);

  const unwrappedRows = unwrapRows(groupEntries[0][1]);

  return unwrappedRows;
}

function getSeriesData(rows, info, isDate) {
  const data = [];
  for (let i = 0; i < rows.length; i++) {
    data.push([
      // если group by является датой то значения по оси икс необходимо сформировать. DAY 1 ...
      isDate ? `${info.groupBy} ${i + 1}` : rows[i][info.groupBy],
      // проверка сделана для того чтобы исключить из значений агрегаций null
      rows[i][info.aggregation[0]] ? rows[i][info.aggregation[0]] : 0,
      rows[i][info.groupBy],
    ]);
  }
  return data;
}

const isDateField = (field = '') =>
  field.includes(types.COHORT) || field === 'permanent.register_date';

function getColumnCategories(rows, isCohort, { groupBy, timezone }) {
  const categories = {};
  for (let i = 0; i < rows.length; i++) {
    categories[rows[i][groupBy]] = null;
  }

  return isCohort
    ? Object.keys(categories).map((category) => toCohortFormat(category, timezone))
    : Object.keys(categories);
}

const getLineCategories = (series, info) =>
  (series.find(({ data }) => !!data.length) || { data: [] }).data.map(
    (_, index) => `${info.groupBy} ${index + 1}`
  );

function convertEventData({ info, result }) {
  let groupsBySplits;
  let isCohort = false;
  const isDate = info.timeUnit !== undefined;
  const getCategories = isDate
    ? (series) => getLineCategories(series, info)
    : () => getColumnCategories(result.rows, isCohort, info);

  if (isDate) {
    groupsBySplits = Object.entries(groupBySplitWithDates(result.rows, info));
  } else {
    isCohort = isDateField(info.groupBy);
    groupsBySplits = Object.entries(groupByUtil(result.rows, (row) => row[info.splitBy]));
  }
  const isSplitCohort = isDateField(info.splitBy);
  const groups = [];
  for (let i = 0; i < groupsBySplits.length; i++) {
    const rows = groupsBySplits[i][1];
    const data = getSeriesData(rows, info, isDate);
    const dataSum = data.reduce((sum, dataItem) => sum + dataItem[1], 0);

    groups.push({
      name: isSplitCohort
        ? toCohortFormat(groupsBySplits[i][0], info.timezone)
        : groupsBySplits[i][0],
      id: i,
      parentId: info.uniqueChar,
      parentName: info.eventName,
      data: isCohort
        ? data.map(([key, v, groupBy]) => [toCohortFormat(key, info.timezone), v, groupBy])
        : data,
      dataSum,
    });
  }

  const globalFrom = info.query.globalFilter.from;
  const globalTo = info.query.globalFilter.to;
  const toGlobal = (date) => moment.tz(date, info.timezone).format(DATE_DAILY_SHORT_FORMAT);

  return {
    name: info.eventName,
    id: info.uniqueChar,
    typeGroupBy: info.typeGroupBy,
    series: groups,
    timeUnit: info.groupBy,
    categories: getCategories(groups),
    periods: `${toGlobal(globalFrom)} - ${moment(toGlobal(globalTo))
      .subtract(1, 'day')
      .format(DATE_DAILY_SHORT_FORMAT)}`,
    // используется для таблицы
    aggregation: info.aggregation,
    isDate,
    isSplitBy: !!info.splitBy,
  };
}

export const toPeriodChartData = (events) => (events || []).map(convertEventData);
